import * as themeCart from '@shopify/theme-cart';
import { getCartSuccess, addToCartFail, setCartActive } from '../store/actions/cart';
import { Cart } from '../util/typings'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store/reducers'
import { dispatchToast } from '../store/actions/toast';

interface ICartItem {
  id: number;
  quantity: number;
  properties?: any
}

interface ICartUpdateByKey {
  quantity: number;
  paraperties?: any
}


/**
 * Cart Fetch custom hook
 */

export const useCart = (): any => {

  const dispatch = useDispatch()

  // selectors
  const active: Cart = useSelector((state: RootState) => state.cart.cartActive)
  const cart: Cart = useSelector((state: RootState) => state.cart.current)

  // get cart
  const getCartState = (): Promise<Cart> => {
    return themeCart.getState()
      .then((cartState: Cart) => {
        if (cartState) {
          dispatch(getCartSuccess(cartState))
          return cartState;
        }
        return false;
      })
      .catch((err: Error) => {
        console.error(err);
        return false;
      })
  }

  /**
   * Adds an Item / Items to cart
   * 
   * @param items the items to add to the cart
   * @param notify whether to display the minicart or not
   * @param callback completion handler
   */
  const addToCart = async (items: ICartItem[], notify = true, callback?: (Cart) => any) => {
    const itemsAddedArray = [];
    const quantityArray = [];
    for (const item of items) {
      const { id, quantity, properties } = item;
      const itemAdded = await themeCart.addItem(id, { quantity, properties })
        .catch(error => {
          dispatch(addToCartFail(error));
          dispatch(dispatchToast('error', error));
        })
      if (itemAdded && itemAdded.id) {
        itemsAddedArray.push(itemAdded.id)
        quantityArray.push(quantity)
      }
    }
    const newCart = await getCartState();

    if (notify && itemsAddedArray.length > 0) {
      dispatch(dispatchToast('cart', itemsAddedArray, quantityArray));
    }
    if (callback) {
      callback(newCart);
    }

    return newCart;
  }

  // Update one item
  const updateCartByKey = async (key: string, opts: ICartUpdateByKey) => {
    if (!key || !opts) { return console.error('A key or options are required to update the cart') }

    let possibleShortStock = false
    try {
      await themeCart.updateItem(key, opts)
      const newCart = await getCartState()
      const updatedItem = newCart.items.find(item => item.key === key)

      if (opts.quantity) {
        // best effort stock availability response
        if (updatedItem.quantity < opts.quantity) {
          possibleShortStock = true
          console.info('The quantity requested may not have been availabl')
        }
      }
      return { newCart, possibleShortStock }

    } catch (error) {
      console.error('Error updating cart:', error.message)
    }
  }

  // Remove one item
  const removeByKey = async (key) => {
    if (!key) { return console.error('A key is required to remove item from the cart') }

    try {

      await themeCart.removeItem(key)
      const newCart = await getCartState()
      return newCart

    } catch (error) {
      console.error('Error removing item from cart:', error.message)
    }
  }

  // clear the items out of the cart
  const clearCart = async () => {
    try {
      await themeCart.clearItems()
      return true
    } catch (error) {
      console.error('Error clearing cart items cart:', error.message)
      return false
    }
  }

  // returns the current cart as reflected in redux
  const safelyReturnCurrentCart = () => {
    return getCartState()
      .then(data => data)
      .catch(() => console.error('cart retrieve error'))
  }

  const setDOMHelpers = () => {
    if (!window['blubolt'].cart) {
      window['blubolt'].cart = {
        currentCart: cart,
        getCurrentCart: safelyReturnCurrentCart,
        syncCart: getCartState,
        addToCart,
        updateItem: updateCartByKey,
        removeItem: removeByKey,
        clear: clearCart,
      };
    }
    return true
  }

  const initializeCart = async () => {
    // Get cart state
    if (!active) {
      await getCartState()
    }
    return dispatch(setCartActive(true))
  }

  useEffect(() => {
    if (active) {
      setDOMHelpers();
    } else {
      initializeCart()
    }
  }, [
    active,
    dispatch,
    cart,
    // add depenmdencies as need for use case
  ]);

  const useCartReturn = {
    cart,
    initializeCart,
    getCartState,
    syncCart: getCartState,
    updateItem: updateCartByKey,
    removeItem: removeByKey,
    addToCart,
    clear: clearCart,
    getCartAttributes: themeCart.getAttributes,
    updateCartAttributes: themeCart.updateAttributes,
    clearCartAttributes: themeCart.clearAttributes,
    getNote: themeCart.getNote,
    updateNote: themeCart.updateNote,
    clearNote: themeCart.clearNote,
    getShippingRates: themeCart.getShippingRates,
  }

  return useCartReturn

}